import imagePlaceholder from '@/assets/placeholder.png';
import Modal from '@/components/Modal';
import Constants from '@/Constants';
import List from '@/models/List';
import { ApplicationState } from '@/store';
import {
    archiveListRequest,
    deleteListRequest,
    publishListRequest,
    toggleListVisibilityClear,
    toggleListVisibilityRequest
} from '@/store/list/action';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Switch from 'react-switch';
import './style.scss';

interface ItemCardProps {
    item: List;
}

const ItemCard = ({ item }: ItemCardProps) => {
    const dispatch = useDispatch();

    const [icon, setIcon] = useState('');
    const [alertText, setAlertText] = useState('');
    const [action, setAction] = useState('');
    const [visibility, setVisibility] = useState(false);
    const { data: strings } = useSelector((state: ApplicationState) => state.locale);
    const { hasError } = useSelector((state: ApplicationState) => state.lists);
    const {
        data: { colors },
    } = useSelector((state: ApplicationState) => state.appOrganization);
    const { data: identity } = useSelector((state: ApplicationState) => state.identity);

    useEffect(() => {
        setVisibility(item.visibility || false);

        if (hasError) {
            setVisibility(!item.visibility);
            dispatch(toggleListVisibilityClear());
        }
    }, [dispatch, hasError, item.visibility]);

    const { id, status } = item;

    const handleModalChange = (icon: string, text: string, action: string) => {
        setIcon(icon);
        setAlertText(text);
        setAction(action);
    };

    const renderActions = () => {
        switch (status) {
            case 'PUBLISHED':
                return (
                    <>
                        <Link className="dropdown-content" to={`/lists/${id}`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('how_to_vote', strings.ARCHIVE_LIST_CONFIRMATION, 'archive')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">how_to_vote</i>
                            <span>{strings.ARCHIVE_ITEM}</span>
                        </div>
                    </>
                );
            case 'DRAFT':
                return (
                    <>
                        <Link className="dropdown-content" to={`/lists/${id}`}>
                            <i className="material-icons va-t m-right--xxs">edit</i>
                            <span>{strings.EDIT}</span>
                        </Link>
                        <div
                            className="dropdown-content"
                            data-target={`#alert-modal-${id}`}
                            data-toggle="modal"
                            onClick={() => handleModalChange('delete', strings.DELETE_LIST_CONFIRMATION, 'delete')}
                            onKeyDown={() => {}}
                            role="button"
                            tabIndex={0}
                        >
                            <i className="material-icons-outlined va-t m-right--xxs">delete</i>
                            <span>{strings.REMOVE}</span>
                        </div>
                    </>
                );
            case 'ARCHIVED':
                return (
                    <div
                        className="dropdown-content"
                        data-target={`#alert-modal-${id}`}
                        data-toggle="modal"
                        onClick={() => handleModalChange('undo', strings.UNARCHIVE_LIST_CONFIRMATION, 'publish')}
                        onKeyDown={() => {}}
                        role="button"
                        tabIndex={0}
                    >
                        <i className="material-icons-outlined va-t m-right--xxs">undo</i>
                        <span>{strings.UNARCHIVE}</span>
                    </div>
                );
            default:
                return null;
        }
    };

    const handleAction = () => {
        switch (action) {
            case 'archive':
                if (item.id) return dispatch(archiveListRequest(item.id));
                break;
            case 'publish':
                if (item.id) return dispatch(publishListRequest(item.id));
                break;
            case 'delete':
                if (item.id) return dispatch(deleteListRequest(item.id));
                break;
            default:
                return null;
        }
        return null;
    };

    const handleVisibility = () => {
        setVisibility((prevState) => !prevState);

        if (item.id) {
            dispatch(toggleListVisibilityRequest(item.id));
        }
    };

    const formatCreatedOn = (value?: Date) => {
        const locales = strings._LANGUAGE === 'pt' ? 'pt-BR' : 'en-US';
        if (!value) return value;
        const data = new Date(value);
        return data.toLocaleDateString(locales);
    };

    const formatWorkload = (value: number) => {
        const dur = moment.duration(value, 'minutes');
        const hours = Math.floor(dur.asHours());
        const mins = Math.floor(dur.asMinutes()) - hours * 60;
        const sec = Math.floor(dur.asSeconds()) - hours * 60 * 60 - mins * 60;

        const result = (hours ? hours + ':' : '') + mins + ':' + (sec > 9 ? sec : '0' + sec);
        return result;
    };

    const formatItemString = () => {
        if (item.lessonAmount) {
            return item.lessonAmount > 1 ? strings.ITEMS : strings.ITEM;
        }
        return strings.ITEM;
    };

    return (
        <>
            <Modal
                content={alertText}
                handleLeftClick={handleAction}
                icon={icon}
                leftButtonText={strings.YES}
                rightButtonText={strings.NO}
                target={`alert-modal-${id}`}
            />
            <div className="item-card-list-list">
                <div className="content-top">
                    <img alt="" src={item.mainImage || imagePlaceholder} />
                    {(item.organizationId !== null || identity.user.admin) && (
                        <div className="dropdown">
                            <i className="material-icons-outlined align-middle more" data-toggle="dropdown">
                                {Constants.icons.MORE_HORIZ}
                            </i>
                            <div className="dropdown-menu dropdown-menu-right">
                                <div className="triangle-container">
                                    <div className="dropdown-triangle" />
                                    <div className="dropdown-triangle-border" />
                                </div>
                                {renderActions()}
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-3 d-flex flex-row w-100">
                    <span className="item-type">{strings.LIST}</span>
                    <span className="ml-auto rate">
                        <i className="material-icons">{Constants.icons.STAR}</i>
                        {(item.rating || 0).toFixed(1)}
                    </span>
                </div>

                <div className="item-card-content">
                    <span className="item-title" title={item.name}>
                        {item.name}
                    </span>
                    <span className="item-info-key">
                        {strings.AUTHOR}:{' '}
                        <span className="item-info-value" title={item.author}>
                            {item.author}
                        </span>
                    </span>
                    <span className="item-info-key">
                        {strings.CREATED_AT}: <span className="item-info-value">{formatCreatedOn(item.createdOn) || '--/--'}</span>
                    </span>
                    <span className="item-info-key">
                        {strings.DURATION}: <span className="item-info-value">{item.workload && formatWorkload(item.workload)}</span>
                    </span>
                </div>

                <div className="item-actions">
                    <span className="item-count">
                        {item.lessonAmount || 0} <span>{formatItemString()}</span>
                    </span>
                    <div className="d-flex">
                        {item.status === 'PUBLISHED' && (item.organizationId !== null || identity.user.admin) && (
                            <>
                                <span>{visibility ? strings.PUBLIC : strings.PRIVATE}</span>
                                <Switch
                                    checked={visibility}
                                    checkedIcon={false}
                                    handleDiameter={20}
                                    height={14}
                                    offColor="#d1d1d1"
                                    offHandleColor="#b3b3b3"
                                    onChange={handleVisibility}
                                    onColor={colors[0]}
                                    onHandleColor={colors[0]}
                                    uncheckedIcon={false}
                                    width={30}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ItemCard;
